import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { CollectiblePiece, Museum, MuseumWing } from '../lib/types';
import { Bar, EmptyState, ErrorState, LoadingGrid, Modal, Panel } from '../components/ui';
import { formatDate, formatNumber } from '../lib/format';

/**
 * The Collection Museum.
 *
 * Undiscovered pieces are shown, as silhouettes with the hint for how to find
 * them — the same decision the Badges page makes, for the same reason. A wing
 * that lists only what you have is a receipt; a wing with visible gaps is the
 * thing that makes somebody go and hatch fifty more eggs.
 *
 * Wings are lazy: only the open one renders its pieces. Eleven wings of six is
 * fine, but the museum is built to grow to hundreds and a page that mounts
 * every piece on load would get slower every time a collectible was added.
 */

export function MuseumPage() {
  const { username } = useParams();
  const state = useAsync<Museum>(
    () => api.get(username ? `/museum/${username}` : '/museum'),
    [username],
  );
  const [open, setOpen] = useState<string | null>(null);
  const [piece, setPiece] = useState<{ piece: CollectiblePiece; wing: MuseumWing } | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={6} height={140} />;

  const { wings, summary } = state.data;
  const core = wings.filter((wing) => !wing.isEvent);
  const seasonal = wings.filter((wing) => wing.isEvent);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">
            {state.data.trainer ? `${state.data.trainer.username}'s Museum` : 'Museum'}
          </h1>
          <div className="page-head__sub">
            {formatNumber(summary.owned)} of {formatNumber(summary.total)} pieces ·{' '}
            {summary.wingsComplete} of {summary.wingsTotal} wings complete
          </div>
        </div>
      </div>

      <Panel raised={false}>
        <div className="stack-sm">
          <Bar ratio={summary.ratio} label={`${Math.round(summary.ratio * 100)}% of the museum`} tall />
          <p className="tiny muted" style={{ margin: 0 }}>
            Seasonal pieces are kept separately and never counted here — a festival you
            were not around for is not a gap in your collection.
            {summary.eventTotal > 0 &&
              ` You have ${summary.eventOwned} of ${summary.eventTotal} seasonal pieces.`}
          </p>
        </div>
      </Panel>

      <div className="museum">
        {core.map((wing) => (
          <Wing
            key={wing.key}
            wing={wing}
            open={open === wing.key}
            onToggle={() => setOpen(open === wing.key ? null : wing.key)}
            onSelect={(selected) => setPiece({ piece: selected, wing })}
          />
        ))}
      </div>

      {seasonal.length > 0 && (
        <>
          <h2 className="section-head">Seasonal</h2>
          <div className="museum">
            {seasonal.map((wing) => (
              <Wing
                key={wing.key}
                wing={wing}
                open={open === wing.key}
                onToggle={() => setOpen(open === wing.key ? null : wing.key)}
                onSelect={(selected) => setPiece({ piece: selected, wing })}
              />
            ))}
          </div>
        </>
      )}

      {piece && (
        <PlaqueModal
          piece={piece.piece}
          wing={piece.wing}
          onClose={() => setPiece(null)}
        />
      )}
    </div>
  );
}

function Wing({
  wing,
  open,
  onToggle,
  onSelect,
}: {
  wing: MuseumWing;
  open: boolean;
  onToggle: () => void;
  onSelect: (piece: CollectiblePiece) => void;
}) {
  return (
    <section className={`wing wing--${wing.accent} ${wing.complete ? 'wing--complete' : ''}`}>
      <button type="button" className="wing__head" onClick={onToggle} aria-expanded={open}>
        <span className={`wing__art wing__art--${wing.art}`} aria-hidden />
        <span className="wing__title">
          <strong>{wing.name}</strong>
          <span className="tiny muted">{wing.description}</span>
        </span>
        <span className="wing__count mono">
          {wing.owned}/{wing.total}
          {wing.complete && <span className="wing__tick" aria-label="complete"> ✓</span>}
        </span>
      </button>

      <div className="wing__bar">
        <div className="wing__fill" style={{ width: `${Math.round(wing.ratio * 100)}%` }} />
      </div>

      {open && (
        <div className="stack-sm" style={{ padding: 'var(--gap-sm)' }}>
          {wing.reward.title && !wing.complete && (
            <p className="tiny muted" style={{ margin: 0 }}>
              Completing this wing unlocks the title <strong>{wing.reward.title}</strong>
              {wing.reward.coins > 0 && ` and ${formatNumber(wing.reward.coins)} coins`}.
            </p>
          )}
          <div className="wing__shelf">
            {wing.pieces.map((piece) => (
              <button
                key={piece.key}
                type="button"
                className={`piece piece--${piece.rarity.toLowerCase()} ${piece.owned ? '' : 'piece--unknown'}`}
                onClick={() => onSelect(piece)}
                style={
                  {
                    '--piece-base': piece.colorBase,
                    '--piece-accent': piece.colorAccent,
                  } as React.CSSProperties
                }
                title={piece.owned ? piece.name : 'Not yet found'}
              >
                <span className={`piece__art piece__art--${piece.art}`} aria-hidden />
                <span className="piece__name">{piece.owned ? piece.name : '???'}</span>
                {piece.quantity > 1 && <span className="piece__dupes mono">x{piece.quantity}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The plaque.
 *
 * A museum piece with no story is inventory, so the lore is the body of this
 * dialog and the statistics are a footnote. An undiscovered piece shows its
 * hint instead — which is the only thing that would actually help.
 */
function PlaqueModal({
  piece,
  wing,
  onClose,
}: {
  piece: CollectiblePiece;
  wing: MuseumWing;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} labelledBy="plaque-title">
      <div className="stack">
        <div className="center">
          <span
            className={`piece__art piece__art--${piece.art} piece__art--large ${piece.owned ? '' : 'piece--unknown'}`}
            style={
              {
                '--piece-base': piece.colorBase,
                '--piece-accent': piece.colorAccent,
              } as React.CSSProperties
            }
            aria-hidden
          />
        </div>
        <h2 id="plaque-title" className="center" style={{ margin: 0 }}>
          {piece.owned ? piece.name : 'Not yet found'}
        </h2>
        <div className="center tiny muted">
          {wing.name} · {piece.rarity.toLowerCase()}
        </div>

        {piece.owned ? (
          <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
            {piece.lore && <p style={{ margin: 0 }}>{piece.lore}</p>}
            {piece.description && <p className="tiny muted" style={{ margin: 0 }}>{piece.description}</p>}
            <div className="tiny muted">
              Found {piece.obtainedAt ? formatDate(piece.obtainedAt) : 'at some point'}
              {piece.quantity > 1 && ` · you have ${piece.quantity}`}
            </div>
          </div>
        ) : (
          <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
            <p style={{ margin: 0 }}>{piece.sourceHint || 'Nobody is entirely sure where this one comes from.'}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Nothing-found state, kept here so the page file owns all its own copy. */
export function EmptyMuseum() {
  return (
    <EmptyState art="▤" title="Your museum is empty">
      Pieces turn up as you play — hatching, helping Community Projects, selling at the
      Bazaar. You are not meant to go looking.
    </EmptyState>
  );
}
